import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { FooterLinksSection, FooterLinksSectionBaseProps } from './FooterLinksSection';
import { FooterMobileStoresBaseProps } from './AdditionalFooterSections/FooterMobileStores';
import { useTheme } from '../../../../theming/useTheme';
import { SocialSectionDataProps } from './AdditionalFooterSections/FooterSocialLinks';
import { generateSeparatorSections } from './footer.utils';
import { ErrorBoundary } from '../../../errorHandling/ErrorBoundary';
import { BottomFooterBar, BottomFooterBarDataProps } from './BottomFooterBar';
import { HUGE_SCREEN_SIZE, MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { BaseUnit } from '../../../../theming/baseUnitDefinition';
import { getAdditionalFooterSection } from './AdditionalFooterSections/additionalFooterSections.utils';

const componentConfigurationKey = 'footer';

const SEPARATOR_HEIGHT = 1;

interface FooterDataProps {
  linksSection: FooterLinksSectionBaseProps | null;
  socialSection: SocialSectionDataProps | null;
  appsSection: FooterMobileStoresBaseProps | null;
  bottomFooterBar: BottomFooterBarDataProps;
}

interface FooterComponentThemeProps {
  backgroundColor: string;
  borderTopColor: string;
  separatorColor: string;
  baseUnit: BaseUnit;
}

const getFooterStyle = (theme: FooterComponentThemeProps) => {
  const { baseUnit, borderTopColor, backgroundColor } = theme;
  return StyleSheet.create({
    footer: {
      borderTop: `solid 2px ${borderTopColor}`,
      [MEDIA_BREAKPOINTS.large]: {
        padding: `0 ${baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        padding: `0 ${baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        padding: `0 ${baseUnit * 0.75}px`,
      },
      margin: '0 auto',
      maxWidth: `${HUGE_SCREEN_SIZE}px`,
    },
    footerWrapper: {
      backgroundColor,
    },
    footerContent: {
      margin: 'auto',
      [MEDIA_BREAKPOINTS.large]: {
        maxWidth: '852px',
      },
    },
  });
};

const getSeparatorStyle = (separatorColor: string, separatorHeight: number) => {
  return StyleSheet.create({
    style: {
      height: `${separatorHeight}px`,
      backgroundColor: separatorColor,
    },
  });
};

const getLinksSection = (linksSection: FooterLinksSectionBaseProps | null) => {
  return linksSection ? <FooterLinksSection key="footer-links" {...linksSection as FooterLinksSectionBaseProps} /> : null;
};

const getFooterItems = (linksSection: FooterLinksSectionBaseProps | null, socialSection: SocialSectionDataProps | null, appsSection: FooterMobileStoresBaseProps | null, separatorColor: string, separatorHeight: number) => {
  return [
    getLinksSection(linksSection),
    getAdditionalFooterSection(socialSection, appsSection, separatorColor, separatorHeight),
  ]
    .filter(section => section !== null)
    .reduce(generateSeparatorSections(getSeparatorStyle(separatorColor, separatorHeight).style), []);
};

export const Footer: React.FunctionComponent<FooterDataProps> = props => {
  const themeProps = useTheme<FooterComponentThemeProps>(componentConfigurationKey);
  const footerComponentStyle = getFooterStyle(themeProps);
  const { linksSection, socialSection, appsSection, bottomFooterBar } = props;
  const { separatorColor } = themeProps;
  return (
    <ErrorBoundary>
      <footer>
        <div className={css(footerComponentStyle.footerWrapper)}>
          <div className={css(footerComponentStyle.footer)}>
            <div className={css(footerComponentStyle.footerContent)}>
              {getFooterItems(linksSection, socialSection, appsSection, separatorColor, SEPARATOR_HEIGHT)}
            </div>
          </div>
        </div>
        <BottomFooterBar {...bottomFooterBar} />
      </footer>
    </ErrorBoundary>
  );
};
